import {Injectable, Injector} from '@angular/core';
import {EntityRepresentation} from '../../../shared/models/visualization/representation.model';
import {NGSIResult} from '../../../shared/models/ngsi/ngsi-result';
import {Layer} from '../../../shared/models/layer.model';
import {IconVisualization} from '../../../shared/models/visualization/icon-visualization.model';
import {DefibrillatorLayerController} from '../../controllers/defibrillator-layer.controller';
import {map, takeUntil} from 'rxjs/operators';
import {forkJoin, Observable} from 'rxjs';
import {NGSIContext} from '../../../shared/models/generic/ngsi-context.model';
import {KPI} from '../../../shared/models/kpi.model';
import {EventService} from '../event.service';
import {BaseComponent} from '../../../shared/components/base.component';
import {SpecialFilterType} from '../../../shared/enums/special-filter-type';
import {NGSIQuery} from '../../../shared/models/ngsi/ngsi-query.model';
import {QueryOptions} from '../../../shared/models/ngsi/query-options';
import {NGSIEntity} from '../../../shared/utils/ngsi-result-util';


export enum DEFIBRILLATOR_COLORS {
    GREEN = 'GREEN',
    RED = 'RED',
    GRAY = 'GRAY',
    YELLOW = 'YELLOW',
    ORANGE = 'ORANGE',
    PURPLE = 'PURPLE',
    BURGUNDY = 'BURGUNDY'
}

@Injectable({ providedIn: 'root' })
export class DefibrillatorLayerService extends BaseComponent {
    public entities: Map<DEFIBRILLATOR_COLORS, NGSIResult[]>;
    private subscribers: DefibrillatorLayerController[];
    static instance: DefibrillatorLayerService;
    public testMap: Map<String, NGSIResult>;
    private isUpdating = false;
    private selectedSource: string = 'All';
    private selectedSpecialFilter: SpecialFilterType = SpecialFilterType.None;
    private padsWithLessThanThreeMonths: string[];

    private constructor(private layer: Layer, private injector: Injector) {
        super(injector);
        this.subscribers = [];
        this.padsWithLessThanThreeMonths = [];
        this.entities = new Map();
        this.testMap = new Map();
        this.getDefibrillatorWithKpi();
    }

    public init(): void {
      this.eventService.eventEmitter
        .pipe(takeUntil(this.destroy$))
        .subscribe(async event => {
          switch (event.id) {
            case EventService.SOURCE_CHANGED: {
              this.selectedSource = event.data;
              this.notifySubscriber();
              break;
            }
            case EventService.SPECIAL_FILTER_CHANGED: {
              this.selectedSpecialFilter = event.data;
              if (this.selectedSpecialFilter !== SpecialFilterType.None) {
                await this.getPadsExpirationDates();
              }
              this.notifySubscriber();
              break;
            }
          }
        });
    }

    public addSubscriber(defibrillatorLayerController: DefibrillatorLayerController) {
      this.subscribers.push(defibrillatorLayerController);
    }

    public unsubscribe(defibrillatorLayerController: DefibrillatorLayerController) {
      const entityIndex = this.subscribers.findIndex(e => {
        return e === defibrillatorLayerController;
      });
      if (!entityIndex) {
        return;
      }
      this.subscribers.splice(entityIndex, 1);
    }

    public notifySubscriber() {
        try {
            this.subscribers.map(controller => {
                controller.getDefibrillators();
            });
        } catch (error) {
            // console.error('Error occurred while notifying layers:', error);
        } finally {
            this.isUpdating = false;
        }
    }

    static getInstance(layer: Layer, injector: Injector) {
        if (DefibrillatorLayerService.instance == null) {
            DefibrillatorLayerService.instance = new DefibrillatorLayerService(layer, injector);
            DefibrillatorLayerService.instance.init();
        }
        return DefibrillatorLayerService.instance;
    }
    static getInstanceDefault() {
      return DefibrillatorLayerService.instance;
    }

    public getDefibrillatorWithKpi() {
        if (this.isUpdating) {
            return;
        }
        const parameters: any = {};
        if (this.userService.isRegularUser()) {
          parameters.companyName = this.userService.companyName.getValue();
        } else {
          parameters.companyName = '*';
        }
        this.isUpdating = true;
        const entitiesFound: Set<string> = new Set();
        this.testMap = new Map<String, NGSIResult>();
        let contextObservable: Observable<any> = null;
        const tempEntities: Map<DEFIBRILLATOR_COLORS, NGSIResult[]> = new Map();

        const defibrillatorTestRef = this.contextService.executeKPI( this.kpiCreationForDefibrillatorTest(), new NGSIContext({
            temporalQuery: {
                timeProperty: 'observedAt',
                timerel: 'between',
                timeExpression: 'now-1d',
                endTimeExpression: 'now'
            }
        }), parameters);
        const defibrillatorPositionRef =  this.contextService.executeKPI( this.kpiCreationForDefibrillatorPosition(), new NGSIContext({
            temporalQuery: {
                timeProperty: 'observedAt',
                timerel: 'between',
                timeExpression: 'now-60d',
                endTimeExpression: 'now'
            }
        }), parameters);

        contextObservable = forkJoin([defibrillatorTestRef, defibrillatorPositionRef]).pipe(
            map(response => {
                return {
                    results: response,
                };
            })
        );
        contextObservable.subscribe( response => {
            this.fillTestMap(response.results[0]);
            response.results[1].forEach((position: NGSIResult) => {
                const refId: string = position.getEntityResult().getElementValue('refId');
                if (!entitiesFound.has(refId)) {
                    entitiesFound.add(refId);
                    if (this.testMap.get(refId) !== undefined) {
                        this.checkResults(this.testMap.get(refId), position, tempEntities);
                    } else {
                        if (!tempEntities.has(DEFIBRILLATOR_COLORS.GRAY)) {
                            tempEntities.set(DEFIBRILLATOR_COLORS.GRAY, [position]);
                        } else {
                            tempEntities.get(DEFIBRILLATOR_COLORS.GRAY).push(position);
                        }
                    }
                }
            });
            this.entities = tempEntities;
            this.notifySubscriber();
          }
        );
    }

    private fillTestMap(responseElement: any) {
        responseElement?.forEach((test: NGSIResult) => {
           const refId = test.getEntityResult().getElementValue('refId');
           if (this.testMap.get(refId) === undefined) {
               this.testMap.set(refId, test);
           }
        });
    }

    public updatePositionColorWithPosition(defibrillatorPosition: NGSIResult) {
      const entitiesFound: Set<string> = new Set();
      const newEntity: Map<DEFIBRILLATOR_COLORS, NGSIResult[]> = new Map();
      const refId: string = defibrillatorPosition.getEntityResult().getElementValue('refId');
      if (!entitiesFound.has(refId)) {
        entitiesFound.add(refId);
        if (this.testMap.get(refId) !== undefined) {
          this.checkResults(this.testMap.get(refId), defibrillatorPosition, newEntity);
        } else {
          if (!newEntity.has(DEFIBRILLATOR_COLORS.GRAY)) {
            newEntity.set(DEFIBRILLATOR_COLORS.GRAY, [defibrillatorPosition]);
          } else {
            newEntity.get(DEFIBRILLATOR_COLORS.GRAY).push(defibrillatorPosition);
          }
        }
      }
      this.removeEntityFromEntitiesMap(refId);
      this.addEntityToEntitiesMap(newEntity);
      this.notifySubscriber();

    }

    public updatePositionColorWithTest(defibrillatorTest: NGSIResult) {
      const refId: string = defibrillatorTest.getEntityResult().getElementValue('refId');
      this.testMap.set(refId, defibrillatorTest); // overrides the existing test
      const defibrillatorPosition = this.getEntityFromEntitiesMap(refId);
      if (defibrillatorPosition) {
        this.removeEntityFromEntitiesMap(refId);
        this.checkResults(this.testMap.get(refId), defibrillatorPosition, this.entities);
        this.notifySubscriber();
      }
    }

  getDefibrillatorsForRepresentation(entityRep: EntityRepresentation): NGSIResult[] {
    const colorString = (entityRep.visualization as IconVisualization).icon.split('_')[1]?.toUpperCase().split('.')[0];
    const colorEnum = this.convertStringToEnum(colorString);

    const colorEntities = this.entities.get(colorEnum);
    if (!colorEntities) return [];

    const isAllSources = this.selectedSource === 'All';
    const isPadsExpiration = this.selectedSpecialFilter === SpecialFilterType.PadsDate;

    return colorEntities.filter(entity => {
      const entityResult = entity.getEntityResult();
      const refId = entityResult.getElementValue('refId');
      const source = entityResult.getElementValue('source');

      const matchesPadsExpiration = !isPadsExpiration || this.padsWithLessThanThreeMonths.includes(refId);
      const matchesSource = isAllSources || source === this.selectedSource;

      return matchesPadsExpiration && matchesSource;
    });
  }

  convertStringToEnum(color: String): DEFIBRILLATOR_COLORS {
        const enumKeys = Object.keys(DEFIBRILLATOR_COLORS).filter(k => typeof DEFIBRILLATOR_COLORS[k as keyof typeof DEFIBRILLATOR_COLORS] === 'string') as (keyof typeof DEFIBRILLATOR_COLORS)[];

        for (const key of enumKeys) {
            if (DEFIBRILLATOR_COLORS[key] === color) {
                return DEFIBRILLATOR_COLORS[key];
            }
        }
        return DEFIBRILLATOR_COLORS.GREEN;
    }

    private removeEntityFromEntitiesMap(refIdToRemove: string): void {
      this.entities.forEach((results, color) => {
        const filteredResults = results.filter(entity => entity.getEntityResult().getElementValue('refId') !== refIdToRemove);
        this.entities.set(color, filteredResults);
      });
    }

    private addEntityToEntitiesMap(newEntity: Map<DEFIBRILLATOR_COLORS, NGSIResult[]>): void {
      newEntity.forEach((newResults, color) => {
        const existingResults = this.entities.get(color) || [];
        this.entities.set(color, [...existingResults, ...newResults]);
      });
    }

    private getEntityFromEntitiesMap(refId: string): NGSIResult | undefined {
      for (const [_, entities] of this.entities.entries()) {
        const match = entities.find(entity => entity.getEntityResult().getElementValue('refId') === refId);
        if (match) {
          return match;
        }
      }
      return undefined;
    }

    private checkResults(entityResponseForDefibrillatorTest: NGSIResult, entityResponseForPosition: NGSIResult, tempEntities: Map<DEFIBRILLATOR_COLORS, NGSIResult[]>) {
        if (entityResponseForPosition.getEntityResult().getElementValue('status') !== null && entityResponseForPosition.getEntityResult().getElementValue('status') === 2) {
            if (!tempEntities.has(DEFIBRILLATOR_COLORS.PURPLE)) {
                tempEntities.set(DEFIBRILLATOR_COLORS.PURPLE, [entityResponseForPosition]);
            } else {
                tempEntities.get(DEFIBRILLATOR_COLORS.PURPLE).push(entityResponseForPosition);
            }
            return;
        }
        if (entityResponseForDefibrillatorTest === undefined) {
            if (!tempEntities.has(DEFIBRILLATOR_COLORS.GRAY)) {
                tempEntities.set(DEFIBRILLATOR_COLORS.GRAY, [entityResponseForPosition]);
            } else {
                tempEntities.get(DEFIBRILLATOR_COLORS.GRAY).push(entityResponseForPosition);
            }
            return;
        }
        const entityResult = entityResponseForDefibrillatorTest.getEntityResult();
        const AEDBatteryVoltageLevel: number = entityResult.getElementValue('AEDDeviceBatteryVoltageLevel') / 10;
        const IOTBatteryVoltageLevel: number = entityResult.getElementValue('IOTModuleBatteryVoltageLevel') / 10;
        const ADSTestResult: number = entityResult.getElementValue('adsTestResult');
        const ECGTestResult: number = entityResult.getElementValue('ecgTestResult');
        const cryptoTestResult: number = entityResult.getElementValue('cryptoTestResult');
        const audioTestResult: number = entityResult.getElementValue('audioTestResult') ;
        const chargeCircuit1TestResult: number = entityResult.getElementValue('chargeCircuit1TestResult') ;
        const chargeCircuit2TestResult: number = entityResult.getElementValue('chargeCircuit2TestResult') ;
        const capacitorVoltageTestResult: number = entityResult.getElementValue('capacitorVoltageTestResult') ;
        const hBridgeCircuit1TestResult: number = entityResult.getElementValue('hBridgeCircuit1TestResult') ;
        const hBridgeCircuit2TestResult: number = entityResult.getElementValue('hBridgeCircuit2TestResult') ;
        const onOffButtonTestResult: number = entityResult.getElementValue('onOffButtonTestResult') ;
        const shockButtonTestResult: number = entityResult.getElementValue('shockButtonTestResult') ;
        const iotAccelerometerTestResult: number = entityResult.getElementValue('iotAccelerometerTestResult') ;
        const iotInternalMemoryTestResult: number = entityResult.getElementValue('iotInternalMemoryTestResult') ;
        const iotAedConnectionTestResult: number = entityResult.getElementValue('iotAedConnectionTestResult') ;
        const padTestResult: number = entityResult.getElementValue('padTestResult') ;
        const ads4v6Result: number = entityResult.getElementValue('ads4v6Result') / 50 ;
        const dacSetVoltage: number = entityResult.getElementValue('dacSetVoltage') ;
        const chargingVoltage: number = entityResult.getElementValue('chargingVoltage') ;
        const chargeTime: number = entityResult.getElementValue('chargeTime') / 10 ;
        const hBridge1: number = entityResult.getElementValue('hBridge1') ;
        const hBridge2: number = entityResult.getElementValue('hBridge2') ;


        const lastTestDate = new Date(
          2000 + entityResult.getElementValue('testYear'),
          entityResult.getElementValue('testMonth') - 1, // JS months = 0–11
          entityResult.getElementValue('testDay'),
          entityResult.getElementValue('testHour'),
          entityResult.getElementValue('testMinute')
        );
        const today = new Date();
        const validTestDate = new Date(today);
        validTestDate.setDate(today.getDate() - 1);

        if (lastTestDate > validTestDate) {
            if (this.layer.representations && this.layer.representations.length > 0) {
                if (AEDBatteryVoltageLevel < 9.5 || ADSTestResult === 0 ||
                    ECGTestResult === 0 || cryptoTestResult === 0 || audioTestResult === 0  || chargeCircuit1TestResult === 0 ||
                    chargeCircuit2TestResult === 0  || capacitorVoltageTestResult === 0 || hBridgeCircuit1TestResult === 0 ||
                    hBridgeCircuit2TestResult === 0 || onOffButtonTestResult === 0 || shockButtonTestResult === 0 ||
                    iotAccelerometerTestResult === 0 || iotInternalMemoryTestResult === 0 || iotAedConnectionTestResult === 0) {
                    if (!tempEntities.has(DEFIBRILLATOR_COLORS.RED)) {
                        tempEntities.set(DEFIBRILLATOR_COLORS.RED, [entityResponseForPosition]);
                    } else {
                        tempEntities.get(DEFIBRILLATOR_COLORS.RED).push(entityResponseForPosition);
                    }

                } else if (this.userService.isAselsanUser() && (((chargingVoltage !== null &&  chargingVoltage !== 0 && (chargingVoltage >= 2300 )) ||
                  (chargeTime !== null && chargeTime !== 0 && (chargeTime >= 12 || chargeTime <= 4 )) ||
                  (ads4v6Result !== null && ads4v6Result !== 0 && (ads4v6Result >= 5.1 || ads4v6Result <= 4.1 )) ||
                  (dacSetVoltage !== null &&  dacSetVoltage !== 0 && (dacSetVoltage >= 2150)) ||
                  (hBridge1 !== null && hBridge1 !== 0 && (hBridge1 > 60 || hBridge1 < 50 )) ||
                  (hBridge2 !== null) && hBridge2 !== 0 && (hBridge2 > 50 || hBridge2 < 40)))) {
                        if (!tempEntities.has(DEFIBRILLATOR_COLORS.BURGUNDY)) {
                          tempEntities.set(DEFIBRILLATOR_COLORS.BURGUNDY, [entityResponseForPosition]);
                        } else {
                          tempEntities.get(DEFIBRILLATOR_COLORS.BURGUNDY).push(entityResponseForPosition);
                        }
                } else if ((AEDBatteryVoltageLevel >= 9.5 && AEDBatteryVoltageLevel < 10 ) || IOTBatteryVoltageLevel < 2.65) {
                    if (!tempEntities.has(DEFIBRILLATOR_COLORS.YELLOW)) {
                        tempEntities.set(DEFIBRILLATOR_COLORS.YELLOW, [entityResponseForPosition]);
                    } else {
                        tempEntities.get(DEFIBRILLATOR_COLORS.YELLOW).push(entityResponseForPosition);
                    }
                } else if (padTestResult === 0) {
                    if (!tempEntities.has(DEFIBRILLATOR_COLORS.ORANGE)) {
                        tempEntities.set(DEFIBRILLATOR_COLORS.ORANGE, [entityResponseForPosition]);
                    } else {
                        tempEntities.get(DEFIBRILLATOR_COLORS.ORANGE).push(entityResponseForPosition);
                    }
                } else if (AEDBatteryVoltageLevel >= 10 && IOTBatteryVoltageLevel >= 2.65 && ADSTestResult === 1 && ECGTestResult === 1 && cryptoTestResult === 1
                    && audioTestResult === 1 && chargeCircuit1TestResult === 1 && chargeCircuit2TestResult === 1
                    && capacitorVoltageTestResult === 1 && hBridgeCircuit1TestResult === 1 && hBridgeCircuit2TestResult === 1 && onOffButtonTestResult === 1
                    && shockButtonTestResult === 1 && iotAccelerometerTestResult === 1 && iotInternalMemoryTestResult === 1 && iotAedConnectionTestResult === 1 && padTestResult === 1) {
                    if (!tempEntities.has(DEFIBRILLATOR_COLORS.GREEN)) {
                        tempEntities.set(DEFIBRILLATOR_COLORS.GREEN, [entityResponseForPosition]);
                    } else {
                        tempEntities.get(DEFIBRILLATOR_COLORS.GREEN).push(entityResponseForPosition);
                    }
                }
            }
        } else {
            if (!tempEntities.has(DEFIBRILLATOR_COLORS.GRAY)) {
                tempEntities.set(DEFIBRILLATOR_COLORS.GRAY, [entityResponseForPosition]);
            } else {
                tempEntities.get(DEFIBRILLATOR_COLORS.GRAY).push(entityResponseForPosition);
            }
        }
    }

    private kpiCreationForDefibrillatorPosition() {
        return new KPI({
            id: '326d8028-ce80-4f62-95b7-80770d24cdc0',
            name: 'Defibilatör Position KPI',
            description: 'Sistemde kayıtlı pozisyon verilerini döner',
            queryType: 'cross-query',
            query: {
                'jsonClass': 'NGSIQuery',
                'filter': {
                    'type': [
                        'DefibrillatorPosition'
                    ]
                },
                limit: 100000,
                cursorAfter: true,
                options: [],
                sortField: 'createdAt',
                sortDirection: 'desc',
                lastNDays: 365
            },
            dimensions: [
                {
                    'keys': {
                        'id': {
                            'dataType': 'URI'
                        }
                    }
                },
                {
                  'keys': {
                    'observedAt': {
                      'dataType': 'DateTime'
                        }
                    }
                }
            ],
            overrideGeoQuery: false,
            overrideTemporalQuery: false,
            overrideIdQuery: false
        });
    }

    private kpiCreationForDefibrillatorTest() {
        return new KPI({
            id: '95ab4b07-0884-4fd3-81e3-0dda8fa6538b',
            name: 'Defibilatör Position KPI',
            description: 'Sistemde kayıtlı test verilerini döner',
            queryType: 'cross-query',
            query: {
                'jsonClass': 'NGSIQuery',
                'filter': {
                    'type': [
                        'DefibrillatorTest'
                    ]
                },
                limit: 100000,
                cursorAfter: true,
                options: [],
                sortField: 'createdAt',
                sortDirection: 'desc',
                lastNDays: 365
            },
            dimensions: [
            ],
            overrideGeoQuery: false,
            overrideTemporalQuery: false,
            overrideIdQuery: false
        });
    }

  private async getPadsExpirationDates() {
    const query: NGSIQuery = new NGSIQuery({
      filter: {
        type: [NGSIEntity.DEFIBRILLATOR] ,
      },
      attrs: [
        {
          jsonClass: 'NGSIPath',
          isTemporal: true,
          propOrRelPath: [
            'pediatricPadExpirationDate'
          ]
        },
        {
          jsonClass: 'NGSIPath',
          isTemporal: true,
          propOrRelPath: [
            'adultPadExpirationDate'
          ]
        }
      ],
      limit: 1000,
      lastNDays: 1000
    });
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    this.contextService.getEntities(query, new QueryOptions(true, false, true)).subscribe(paginatedResult => {
      paginatedResult.results.forEach(result => {
        const entityResult = result.getEntityResult();

        const adultPadDateStr = entityResult.getSingleElementValue('adultPadExpirationDate');
        const pediatricPadDateStr = entityResult.getSingleElementValue('pediatricPadExpirationDate');

        const adultPadDate = adultPadDateStr ? new Date(adultPadDateStr) : null;
        const pediatricPadDate = pediatricPadDateStr ? new Date(pediatricPadDateStr) : null;

        const isAdultPadExpiring = adultPadDate && adultPadDate <= threeMonthsFromNow;
        const isPediatricPadExpiring = pediatricPadDate && pediatricPadDate <= threeMonthsFromNow;

        if (isAdultPadExpiring || isPediatricPadExpiring) {
          this.padsWithLessThanThreeMonths.push(result.getEntityId());
        }
      });
      this.notifySubscriber();
    });
  }
}
